<template>
  <v-dialog
    v-model="dialog" max-width="70vw"
    @keydown.enter="save"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn v-if="canAdd"
        class="mx-2" color="primary"
        fab icon small
        v-bind="attrs" v-on="on"
        @click="newItem"
      >
        <v-icon>
          {{icons.mdiPlus}}
        </v-icon>
      </v-btn>
    </template>
    <v-card>
      <v-card-title>
        <span class="subtitle-1">{{ dialogTitle }}</span>
      </v-card-title>

      <v-card-text>
        <v-container :class="containerClass">
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="editedItem.username"
                label="Username"
                id="userAdminSetUsername"
                name="userAdminSetUsername"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-row no-gutters align="center">
                <v-col>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-bind="attrs" v-on="on"
                        v-model="editedItem.password"
                        label="Password"
                        type="password"
                        id="userAdminSetPassword"
                        name="userAdminSetPassword"
                        autocomplete="new-password"
                      ></v-text-field>
                    </template>
                    <span>This user will be asked to change their password when they login.</span>
                  </v-tooltip>
                </v-col>
                <v-col cols="auto" class="ml-2 mb-2" v-if="editedIndex !== -1">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs" v-on="on"
                        small color="warning"
                        @click="resetPassword"
                      >
                        Reset Password to: 1234
                      </v-btn>
                    </template>
                    <span>This user will be asked to change their password when they login.</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="editedItem.fullname"
                label="Fullname"
                id="userAdminSetFullname"
                name="userAdminSetFullname"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-select id="tmc-user-roles"
                dense
                v-model="selectedRoles"
                :items="rolesList"
                label="Roles"
                item-value="name"
                item-text="description"
                chips
                multiple
              ></v-select>
              <v-alert
                v-if="hasUnregisteredDepositRole"
                dense
                outlined
                type="warning"
                class="mt-n2 mb-0 caption"
              >
                "Unregistered Deposits User" only applies to users outside the treasurer's office.
                Do not select this for normal treasurer's office users.
              </v-alert>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="6">
              <v-text-field
                v-model.number="editedItem.userWkst"
                label="Workstation #"
                type="number" />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="editedItem.phoneNumber"
                label="Phone Number"
                id="userAdminSetPhoneNumber"
                name="userAdminSetPhoneNumber"
                :rules="phoneRules"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="editedItem.email"
                label="Email"
                id="userAdminSetEmail"
                name="userAdminSetEmail"
                type="email"
                :rules="emailRules"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-checkbox
                v-model="editedItem.disableUser"
                label="Disable User"
              ></v-checkbox>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          text color="blue darken-1"
          @click="close"
        >
          Cancel
        </v-btn>
        <v-btn
          id="userAdministrationSaveBtn"
          text color="blue darken-1"
          @click="save"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  mdiPlus,
} from '@mdi/js';
import { mapGetters } from 'vuex';

const TM_ADMIN_ROLE_DESCRIPTIONS = [
  'TM Consulting Support / Administrative role',
  'TM Restore Database',
  'Auditor role for history viewing, etc',
];

const UNREGISTERED_DEPOSIT_ROLE_NAME = 'UNREGISTEREDDEPOSITUSER';

const fields = {
};

export default {
  name: 'NewEditDialog',
  props: ['canAdd'],
  data: () => ({
    icons: {
      mdiPlus,
    },
    defaultItem: { ...fields },
    selectedRoles: [],
    emailRules: [
      (v) => !v || /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || 'Invalid email format',
    ],
    phoneRules: [
      (v) => !v || /^\d*$/.test(v) || 'Phone number must contain digits only',
    ],
  }),
  computed: {
    ...mapGetters('user', ['getSettings']),
    ...mapGetters('auth/token', ['authUserRoles']),
    editedIndex() {
      return this.$store.state.UserAdministration.editedIndex;
    },
    editedItem() {
      return {
        ...this.$store.state.UserAdministration.editedItem,
      };
    },
    dialogTitle() {
      return this.editedIndex === -1 ? 'New Item' : 'Edit Item';
    },
    dialog() {
      return this.$store.state.UserAdministration.dialog;
    },
    rolesList() {
      const { rolesList: raw } = this.$store.state.UserAdministration;
      const list = (this.authUserRoles || []).includes('TmcSupport')
        ? [...raw]
        : raw.filter(
          (role) => !TM_ADMIN_ROLE_DESCRIPTIONS.includes(role.description),
        );
      return list.sort((a, b) => (a.description || '').localeCompare(
        b.description || '',
        undefined,
        { sensitivity: 'base' },
      ));
    },
    rolesConst() {
      return this.$store.state.UserAdministration.rolesConst;
    },
    containerClass() {
      return this.getSettings.lightdark === 'light' ? 'grey lighten-5' : '';
    },
    hasUnregisteredDepositRole() {
      return this.selectedRoles.includes(UNREGISTERED_DEPOSIT_ROLE_NAME);
    },
  },
  watch: {
    editedItem: {
      immediate: true,
      handler(item) {
        this.selectedRoles = [...(item.roles || [])];
      },
    },
  },
  created() {
  },
  methods: {
    resetPassword() {
      if (window.confirm('Reset this user\'s password to "1234"? They will be prompted to change it on their next login.')) {
        this.$store.commit('UserAdministration/setEditedItem', {
          ...this.editedItem,
          password: '1234',
        });
      }
    },
    newItem() {
      this.$store.commit('UserAdministration/setEditedIndex', -1);
      this.$store.commit('UserAdministration/setEditedItem', { ...this.defaultItem });
      this.$store.commit('UserAdministration/setDialog', true);
    },
    close() {
      this.$store.commit('UserAdministration/setDialog', false);
      this.$nextTick(() => {
        this.$store.commit('UserAdministration/setEditedItem', { ...this.defaultItem });
        this.$store.commit('UserAdministration/setEditedIndex', -1);
      });
    },
    save() {
      if (this.hasUnregisteredDepositRole
        && !window.confirm('This user has the "Unregistered Deposits User" role, which only applies to users outside the treasurer\'s office. Continue saving?')) {
        return;
      }
      const userData = { ...this.editedItem, roles: this.selectedRoles };
      // Validate email format
      const isEmailValid = !userData.email || /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(userData.email);
      if (!isEmailValid) {
        return;
      }

      // Validate phone number format
      const isPhoneValid = !userData.phoneNumber || /^\d*$/.test(userData.phoneNumber);
      if (!isPhoneValid) {
        return;
      }
      userData.pwdhash = userData.password || '';
      userData.userWkst = userData.userWkst || null;
      userData.email = userData.email || null;
      userData.phoneNumber = userData.phoneNumber || null;
      this.$delete(userData, 'password');
      this.$store.dispatch('UserAdministration/upsertUser', userData);
      this.close();
    },
  },
};
</script>
